import { useLocale as useLocaleR42 } from '@/lib/locale';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, useAnimationControls, useInView } from 'framer-motion';
import { RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import '@/styles/splash-water.css';

// The clean mark is used in every state, including reduced motion and fallback.
// Never restore the original PNG here: its water is baked into the artwork.
const MARK = '/mascot/r24-logo-clean.webp';
const MARK_FALLBACK = '/mascot/r24-logo-clean.png';
const CROWN = '/mascot/r24-water-fan.webp';
const LIP = '/mascot/r24-water-front.webp';
const RIPPLE = '/mascot/r23-water-ripple.webp';
const WATERLINE = 416 / 512;
const TEXTURE_WATERLINE = .70;
// User-requested literal 4× impact footprint; logo and typography stay untouched.
const IMPACT_SCALE = 4;
const IMPACT_DROP_SCALE = 2;
const FRAMES = Array.from({ length: 17 }, (_, i) => i / 16);
const DROPS = [
    [-.96, .64, 5, .86], [-.78, .87, 7, 1.04], [-.59, .61, 4, .92],
    [-.34, .98, 5, 1.1], [-.12, .72, 3, .96], [.17, .86, 4, 1.06],
    [.38, .66, 6, .91], [.57, 1, 4, 1.14], [.79, .72, 6, .98], [.96, .58, 4, .84],
];

// Wrappers own positioning. Motion children only scale/rotate: no competing
// translateX(-50%) that Framer can overwrite. Every water layer uses one origin.
const WaterTexture = ({ src, width, kind, front = false }) => (
    <div className={`splash-texture-frame ${front ? 'splash-water-front' : 'splash-water-rear'}`} style={{ width, height: width, left: -width / 2, top: -width * TEXTURE_WATERLINE }} data-testid={`splash-${kind}-frame`}>
        <motion.img src={src} width="512" height="512" alt="" aria-hidden="true" className="splash-water-texture"
            style={{ transformOrigin: `50% ${TEXTURE_WATERLINE * 100}%` }}
            initial={{ opacity: 0, scaleX: .56, scaleY: .06 }}
            animate={kind === 'ripple'
                ? { opacity: [0, .28, .16, 0], scaleX: [.58, .88, 1.1, 1.24], scaleY: [.18, .38, .46, .5] }
                : { opacity: [0, front ? .78 : .94, .44, 0], scaleX: [.56, 1, 1.08, 1.18], scaleY: front ? [.06, .52, .36, .08] : [.06, .8, .46, .1] }}
            transition={{ duration: kind === 'ripple' ? 2.1 : 1.85, delay: kind === 'ripple' ? .06 : 0, times: [0, .16, .56, 1], ease: 'easeOut' }}
            data-testid={`splash-${kind}`} />
    </div>
);

export function SplashLogoAnimation({ size = 220, light = false, className = '', onImpact, onCancel }) {
    const { t: t42 } = useLocaleR42();
    const stageRef = useRef(null);
    const impactRef = useRef(null);
    const visible = useInView(stageRef, { amount: .2 });
    const controls = useAnimationControls();
    const callbacks = useRef({ onImpact, onCancel });
    const generation = useRef(0);
    const lastRun = useRef(-1);
    const [run, setRun] = useState(0);
    const [phase, setPhase] = useState('loading');
    const [effects, setEffects] = useState(null);
    const [assets, setAssets] = useState(null);
    const [stageWidth, setStageWidth] = useState(size * 2.4);
    const [reduced, setReduced] = useState(() => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    const [pageVisible, setPageVisible] = useState(() => typeof document === 'undefined' || !document.hidden);
    const stageHeight = Math.round(size * 1.10);
    const logoTop = stageHeight - size - 8;
    const contactY = logoTop + size * WATERLINE;
    // Multiply AFTER the previous responsive limits: every texture is exactly
    // four times the R24 width/height. Only the hero boundary clips the big burst.
    const crownWidth = Math.min(size * .82, (stageWidth - 12) / 1.18) * IMPACT_SCALE;
    const rippleWidth = Math.min(size * .82, (stageWidth - 8) / 1.24) * IMPACT_SCALE;
    const spread = Math.min(size * .52, stageWidth / 2 - 10) * IMPACT_SCALE;

    useEffect(() => { callbacks.current = { onImpact, onCancel }; }, [onImpact, onCancel]);
    useEffect(() => {
        let current = true;
        const paths = [MARK, MARK_FALLBACK, CROWN, LIP, RIPPLE];
        Promise.all(paths.map((src) => new Promise((resolve) => {
            const image = new Image();
            image.onload = () => resolve([src, true]);
            image.onerror = () => resolve([src, false]);
            image.src = src;
        }))).then((loaded) => { if (current) setAssets(Object.fromEntries(loaded)); });
        return () => { current = false; };
    }, []);
    useEffect(() => {
        const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
        const updatePreference = () => setReduced(preference.matches);
        const updateVisibility = () => setPageVisible(!document.hidden);
        const updateWidth = () => setStageWidth(stageRef.current?.clientWidth || size * 2.4);
        updatePreference(); updateVisibility(); updateWidth();
        preference.addEventListener('change', updatePreference);
        document.addEventListener('visibilitychange', updateVisibility);
        const observer = new ResizeObserver(updateWidth);
        if (stageRef.current) observer.observe(stageRef.current);
        return () => {
            preference.removeEventListener('change', updatePreference);
            document.removeEventListener('visibilitychange', updateVisibility);
            observer.disconnect();
        };
    }, [size]);

    useEffect(() => {
        const ticket = ++generation.current;
        let current = true;
        const stop = () => {
            controls.stop();
            controls.set({ y: 0, rotate: 0, scaleX: 1, scaleY: 1, opacity: 1 });
            setPhase('settled'); setEffects(null);
        };
        const rock = () => {
            if (!current || generation.current !== ticket) return;
            setPhase('rocking');
            // A buoyant waterline pivot, not a rotating image-center or elastic wobble.
            // Endpoints match so each 4.8-second cycle joins smoothly.
            controls.start({
                rotate: [0, -1.8, 0, 1.8, 0],
                y: [0, 2, 0, -2, 0],
                scaleX: 1, scaleY: 1,
                transition: { duration: 4.8, times: [0, .25, .5, .75, 1], ease: 'easeInOut', repeat: Infinity },
            });
        };
        const cleanup = () => { current = false; generation.current += 1; controls.stop(); };
        if (!assets) return cleanup;
        if (reduced || !pageVisible || (!assets[MARK] && !assets[MARK_FALLBACK])) {
            stop(); callbacks.current.onCancel?.();
            return cleanup;
        }
        if (!visible) { stop(); return cleanup; }
        // Returning to view resumes the boat motion without another crash/cascade.
        if (lastRun.current === run) { stop(); rock(); return cleanup; }
        lastRun.current = run;
        callbacks.current.onCancel?.();
        setEffects(null); setPhase('falling');
        controls.set({ y: -size * 1.35, rotate: -4, scaleX: 1, scaleY: 1, opacity: 1 });
        const land = async () => {
            await controls.start({ y: 0, rotate: 0, transition: { duration: .68, ease: [.32, 0, .7, .3] } });
            if (!current || generation.current !== ticket) return;
            setPhase('impact'); setEffects({ id: ticket });
            const point = impactRef.current?.getBoundingClientRect();
            if (point) callbacks.current.onImpact?.({ x: point.left, y: point.top, size, runId: ticket });
            await controls.start({
                y: [0, size * .011, -size * .006, 0],
                rotate: [0, -.8, .45, 0],
                scaleX: [1, 1.018, 1.004, 1], scaleY: [1, .978, 1.004, 1],
                transition: { duration: .84, times: [0, .2, .65, 1], ease: 'easeOut' },
            });
            rock();
        };
        land();
        return cleanup;
    }, [assets, controls, pageVisible, reduced, run, size, visible]);

    const replay = useCallback(() => {
        generation.current += 1;
        controls.stop();
        callbacks.current.onCancel?.();
        setEffects(null);
        setRun((previous) => previous + 1);
    }, [controls]);
    const logoSource = assets?.[MARK] === false ? MARK_FALLBACK : MARK;

    return <div className={`splash-water-theme splash-logo-composition ${className}`} style={{ width: size * 2.4 }} data-testid="splash-logo-animation" data-phase={phase}>
        <div ref={stageRef} className="splash-logo-stage" style={{ height: stageHeight }} data-testid="splash-logo-stage">
            <div ref={impactRef} className="splash-impact-point" style={{ top: contactY }} data-testid="splash-impact-anchor" />
            {effects && !reduced && <motion.div key={effects.id} className="splash-local-water" style={{ top: contactY }} aria-hidden="true" data-testid="splash-water-effects" data-impact-scale={IMPACT_SCALE}
                initial={{ opacity: 1 }} animate={{ opacity: [1, 1, 0] }} transition={{ duration: 2.2, times: [0, .82, 1] }}
                onAnimationComplete={() => setEffects((active) => active?.id === effects.id ? null : active)}>
                {assets?.[CROWN] && <WaterTexture src={CROWN} width={crownWidth} kind="crown" />}
                {assets?.[RIPPLE] && <WaterTexture src={RIPPLE} width={rippleWidth} kind="ripple" />}
                {DROPS.map(([direction, height, diameter, duration], index) => <motion.span key={index} className="splash-fluid-drop" data-testid={`splash-impact-drop-${index}`} style={{ width: diameter * IMPACT_DROP_SCALE, height: diameter * 1.45 * IMPACT_DROP_SCALE }}
                    initial={{ x: 0, y: 0, opacity: 0 }}
                    animate={{ x: FRAMES.map((t) => direction * spread * t), y: FRAMES.map((t) => -4 * size * .22 * IMPACT_SCALE * height * t * (1 - t)), opacity: [0, .72, .48, 0], rotate: [direction * 22, 0] }}
                    transition={{ duration, delay: .035 + index % 3 * .018, x: { duration, ease: 'linear' }, y: { duration, ease: 'linear' }, opacity: { duration, times: [0, .14, .7, 1] } }} />)}
            </motion.div>}
            <div className="splash-logo-fall-clip" data-testid="splash-logo-fall-clip">
                <div className="splash-logo-anchor" style={{ top: logoTop, width: size, height: size }}>
                    <Button variant="ghost" className="splash-logo-trigger h-full w-full p-0" onClick={replay} aria-label={t42("Replay SplashPad logo splash")} data-testid="splash-logo-trigger">
                        <motion.img src={logoSource} width="512" height="512" alt="SplashPad" className="splash-logo-mark" data-testid="splash-logo-image" draggable="false"
                            style={{ transformOrigin: `50% ${WATERLINE * 100}%` }}
                            initial={{ y: reduced ? 0 : -size * 1.35, opacity: reduced ? 1 : 0 }} animate={controls} />
                    </Button>
                </div>
            </div>
            {effects && !reduced && assets?.[LIP] && <div className="splash-local-water splash-local-front" style={{ top: contactY }} aria-hidden="true" data-testid="splash-water-front-layer"><WaterTexture src={LIP} width={crownWidth} kind="lip" front /></div>}
        </div>
        <motion.div className="splash-logo-caption" initial={false} animate={{ opacity: phase === 'falling' || phase === 'loading' ? 0 : 1 }} transition={{ duration: reduced ? 0 : .2 }}>
            <span className={`splash-logo-catchphrase font-bold ${size >= 320 ? 'text-2xl' : 'text-sm sm:text-base'}`} data-testid="splash-logo-catchphrase"><span className={light ? 'text-white/70' : 'text-slate-500'}>{t42("Make a")} </span><span className="text-[color:var(--sp-ocean)]">{t42("Splash")}</span><span className={light ? 'text-white/70' : 'text-slate-500'}> {t42("with")} </span><span className="text-[color:var(--sp-ocean)]">{t42("SplashPad")}</span></span>
            <Button variant="ghost" size="icon" className="splash-replay-button" onClick={replay} aria-label={t42("Replay splash animation")} data-testid="splash-replay-button"><RotateCw aria-hidden="true" className="h-4 w-4" /></Button>
        </motion.div>
    </div>;
}

export default SplashLogoAnimation;
